import React, { useState } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { supabase } from '../../../services/supabase';

interface TableResult {
  orphan_users: number;
  deleted_rows: number;
  truncated: boolean;
}

interface SweepResponse {
  success: boolean;
  valid_users: number;
  total_deleted: number;
  results: Record<string, TableResult>;
  error?: string;
}

type SweepState = 'confirm' | 'running' | 'complete' | 'error';

interface OrphanedUserDataDialogProps {
  open: boolean;
  onClose: () => void;
}

export const OrphanedUserDataDialog: React.FC<OrphanedUserDataDialogProps> = ({
  open,
  onClose,
}) => {
  const [state, setState] = useState<SweepState>('confirm');
  const [result, setResult] = useState<SweepResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Reset when the dialog opens
  React.useEffect(() => {
    if (open) {
      setState('confirm');
      setResult(null);
      setError(null);
    }
  }, [open]);

  const handleRun = async () => {
    setState('running');
    setError(null);

    try {
      const { data, error: invokeError } = await supabase.functions.invoke(
        'sweep-orphaned-user-rows',
        { body: {} }
      );

      if (invokeError) {
        throw invokeError;
      }

      const sweepResult: SweepResponse = data;
      if (!sweepResult.success) {
        throw new Error(sweepResult.error || 'Sweep failed');
      }

      setResult(sweepResult);
      setState('complete');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to sweep orphaned rows');
      setState('error');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Delete Orphaned User Data</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {state === 'confirm' && (
          <Typography variant="body2" color="text.secondary">
            This scans user-keyed tables that are not linked to <code>auth.users</code>{' '}
            (user_events, user_listened_stories, user_show_prefs, user_muted_shows,
            exposures) and removes rows whose user no longer exists — leftovers from
            an interrupted account deletion.
          </Typography>
        )}

        {state === 'running' && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
            <CircularProgress size={24} />
            <Typography variant="body1">Scanning and deleting orphaned rows...</Typography>
          </Box>
        )}

        {state === 'complete' && result && (
          <Box>
            <Typography variant="subtitle1" gutterBottom>
              Deleted {result.total_deleted} orphaned row
              {result.total_deleted === 1 ? '' : 's'} ({result.valid_users} valid users).
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Table</TableCell>
                  <TableCell align="right">Orphan users</TableCell>
                  <TableCell align="right">Rows deleted</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(result.results).map(([table, r]) => (
                  <TableRow key={table}>
                    <TableCell>
                      {table}
                      {r.truncated && ' (truncated — re-run)'}
                    </TableCell>
                    <TableCell align="right">{r.orphan_users}</TableCell>
                    <TableCell align="right">{r.deleted_rows}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {state === 'complete' || state === 'error' ? (
          <Button onClick={onClose}>Close</Button>
        ) : (
          <>
            <Button onClick={onClose} disabled={state === 'running'}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleRun}
              disabled={state === 'running'}
              startIcon={state === 'running' ? <CircularProgress size={20} /> : null}
            >
              {state === 'running' ? 'Running...' : 'Scan & Delete Orphans'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};
